import { AsyncPipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Inject, input, OnInit, Optional } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  AppCustomerNetworkRiskScoreSettings,
  AppCustomerNetworkRiskScoreSettingsResultCategory,
  NetworkRiskScore,
  NetworkRiskScoreSource,
} from '@nk/data-entities-lib';
import { ButtonDirective, ErrorAlertComponent, isHttpErrorResponse } from '@nk/ui-lib';
import { Dict, LOADING_STATE_HANDLER_SERVICE_TOKEN, LoadingStateHandlerService } from '@nk/utils-lib';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { BehaviorSubject, catchError, combineLatest, Observable, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { PrettyCompanyNamePipe } from '../../pipes/pretty-company-name.pipe';
import { NetworkRiskScoreService } from '../../services/network-risk-score.service';
import {
  isNetworkRiskGroupKey,
  NETWORK_RISK_SCORE_COLOR,
  NETWORK_RISK_SCORE_TRANSLATIONS,
} from '../../utils/network-risk-score.utils';
import { resolveNetworkRiskResultCategory } from '../company-analysis-network-risk-card/company-analysis-network-risk.utils';
import { CompanyAnalysisRiskLayoutComponent } from '../company-analysis-risk-layout/company-analysis-risk-layout.component';
import { CompanyAnalysisRiskLoadingComponent } from '../company-analysis-risk-loading/company-analysis-risk-loading.component';
import { RiskLevelIconComponent } from '../risk-level-icon/risk-level-icon.component';
import { NetworkRiskScoreRiskColorClassPipe } from '../../pipes/network-risk-score-risk-color-class.pipe';

@Component({
  selector: 'nk-company-analysis-network-risk-score',
  imports: [
    AsyncPipe,
    DecimalPipe,
    TranslateModule,
    RouterModule,
    ErrorAlertComponent,
    CompanyAnalysisRiskLoadingComponent,
    ButtonDirective,
    RiskLevelIconComponent,
    PrettyCompanyNamePipe,
    CompanyAnalysisRiskLayoutComponent,
    NetworkRiskScoreRiskColorClassPipe,
  ],
  templateUrl: './company-analysis-network-risk-score.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class CompanyAnalysisNetworkRiskScoreComponent implements OnInit {
  public companyIdentifier = input.required<string>();
  public showRiskScoreOnDefault = input(false); // Used by the report
  public defaultListSize = input(3);
  public displayShowMoreButton = input(true);
  public printVersion = input(false);

  public networkRiskScore$!: Observable<NetworkRiskScore>;
  public showRiskScore = false;
  public listSize!: number;
  public stepSize = 10;
  public networkRiskSettings$: Observable<AppCustomerNetworkRiskScoreSettings> =
    this._networkRiskScoreService.appCustomerNetworkRiskScoreSettingCache.get().pipe(map(([first]) => first));
  private _loading = new BehaviorSubject(true);
  public loading$ = this._loading.asObservable();
  public error!: HttpErrorResponse;
  public companyNetworkRiskScoreColor: Dict<string> = NETWORK_RISK_SCORE_COLOR;
  public companyNetworkRiskScoreTranslations: Dict<string> = NETWORK_RISK_SCORE_TRANSLATIONS;
  public networkRiskScoreData: {
    networkRiskScore: NetworkRiskScore;
    networkRiskScoreSettings: AppCustomerNetworkRiskScoreSettings;
    networkRiskScoreCategory: AppCustomerNetworkRiskScoreSettingsResultCategory;
  } | null = null;
  public allNetworkRiskScoreSourcesSorted: NetworkRiskScoreSource[] | undefined;

  constructor(
    @Optional()
    @Inject(LOADING_STATE_HANDLER_SERVICE_TOKEN)
    private _reportLoadingStateService: LoadingStateHandlerService,
    private _networkRiskScoreService: NetworkRiskScoreService,
    private _bsModalRef: BsModalRef,
  ) {}

  public ngOnInit(): void {
    this.listSize = this.defaultListSize();
    this.showRiskScore = this.showRiskScoreOnDefault();
    if (this._reportLoadingStateService) {
      this._reportLoadingStateService.setLoadingState(this.constructor.name, true);
    }
    this.networkRiskScore$ = this._networkRiskScoreService.companyNetworkRiskScoreCache
      .get(this.companyIdentifier())
      .pipe(map(([first]) => first));

    combineLatest([this.networkRiskSettings$, this.networkRiskScore$])
      .pipe(
        map(([settings, score]) => {
          if (!settings || !score) {
            console.error('No network found for network risk score calculation', score, settings);
            return null;
          }
          const category = resolveNetworkRiskResultCategory(score.network_risk_score, settings.result_category);

          if (!isNetworkRiskGroupKey('category.result_category_key')) {
            console.warn(`Unknown network risk category: ${category.result_category_key}`);
          }
          return {
            networkRiskScore: score,
            networkRiskScoreSettings: settings,
            networkRiskScoreCategory: category,
          };
        }),
        tap(() => {
          this._loading.next(false);
          if (this._reportLoadingStateService) {
            this._reportLoadingStateService.setLoadingState(this.constructor.name, false);
          }
        }),
        catchError((error: unknown) => {
          this._loading.next(false);
          if (this._reportLoadingStateService) {
            this._reportLoadingStateService.setLoadingState(this.constructor.name, false);
          }
          if (isHttpErrorResponse(error)) {
            this.error = error;
          }
          return throwError(() => error);
        }),
      )
      .subscribe((networkRiskScore) => {
        if (networkRiskScore) {
          const mainEntity: NetworkRiskScoreSource = {
            entity_key: networkRiskScore.networkRiskScore.entity_key,
            entity_name: networkRiskScore.networkRiskScore.entity_name,
            entity_uuid: networkRiskScore.networkRiskScore.entity_uuid,
            entity_contributing_score: networkRiskScore.networkRiskScore.entity_direct_network_risk_score,
          };
          const allNetworkRiskScoreSources = [
            mainEntity,
            ...(networkRiskScore?.networkRiskScore.network_risk_sources ?? []),
          ];
          this.allNetworkRiskScoreSourcesSorted = allNetworkRiskScoreSources
            .slice()
            .sort((a, b) => b.entity_contributing_score - a.entity_contributing_score);
        }

        this.networkRiskScoreData = networkRiskScore;
        this._loading.next(false);
      });
  }

  public increaseList(): void {
    this.listSize += this.stepSize;
  }

  public resetList(): void {
    this.listSize = this.defaultListSize();
  }

  public routerLinkClicked(): void {
    this._bsModalRef.hide();
  }
}
